import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { CaseModal } from '../components/CaseModal';
import { casesApi } from '../casesApi';
import { CASE_AUDIT_ENTITY_TYPES, CASE_STATUS_LABEL, FLAG, hasFlag, type CaseListItem } from '../caseTypes';
import { useEmployeePrerequisite } from '../useEmployeePrerequisite';
import { heldEvidenceReason } from '../utils/caseReasons';
import { failedFilesMessage } from '../utils/fileAll';

const EMPLOYEE_DETAIL =
  "Case Management is open only to employees, and your account is not linked to an employee's personnel record — so you cannot register a case or be added to one. Link it on the personnel record's Account card, or ask HR to.";

/**
 * The case REGISTRY — every case the reader is in, created or shared with them. Case Management
 * is the parent of every case-related record (product call, 12 Oct 2026); Evidence files its
 * custody under a case registered here and opens none of its own.
 *
 * <b>A case is private, so this is not every case.</b> A case nobody shared with the reader is not
 * listed at all: it reads as absent, the way a private Repository folder does. A case they ARE in
 * but whose access level is above their clearance is listed, counted and MASKED — the title is
 * asterisks, fixed length, and the server does not send it.
 *
 * Row actions carry the KEY and are disabled for the case's own FLAG — the key says the account
 * may do this kind of thing, the flag says whether this case allows it.
 */
export function CasesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CaseListItem | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'deactivate' | 'activate'; record: CaseListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // The employee rule, stated before Create refuses — not only in the refusal.
  const employee = useEmployeePrerequisite(casesApi.eligibility, EMPLOYEE_DETAIL);

  const fetchCases = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      casesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          caseStatus: query.facets.caseStatus?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (record: CaseListItem): MenuItem[] => [
      {
        key: `edit-${record.id}`,
        label: 'Edit',
        permission: 'cases.update',
        disabledReason: record.isRestricted
          ? 'This case is above your access level.'
          : !hasFlag(record.permissions, FLAG.edit)
            ? 'You have not been given Edit on this case.'
            : null,
        onClick: () => setEditing(record),
      },
      {
        key: `restore-${record.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'cases.delete',
        disabledReason: record.isActive
          ? 'This case is not deleted.'
          : !hasFlag(record.permissions, FLAG.delete)
            ? 'You have not been given Delete on this case.'
            : null,
        onClick: () => setConfirming({ kind: 'activate', record }),
      },
    ],
    [],
  );

  async function runConfirmed(reauthentication?: string) {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await casesApi.deactivate(confirming.record.id, reauthentication ?? '');
        toast.success(`${confirming.record.caseNumber} was deleted.`);
      } else {
        await casesApi.activate(confirming.record.id);
        toast.success(`${confirming.record.caseNumber} was restored.`);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<CaseListItem>
        module="cases"
        breadcrumb={['Case Management', 'Cases']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'caseStatus', label: 'Case status' },
        ]}
        auditEntityTypes={CASE_AUDIT_ENTITY_TYPES}
        rowKey={(record) => record.id}
        fetch={fetchCases}
        reloadKey={reloadKey}
        searchPlaceholder="Search case number or title"
        emptyMessage="No cases yet — or none shared with you. A case is visible only to the people it is shared with."
        prerequisites={[employee]}
        onCreate={() => setCreating(true)}
        createPermission="cases.create"
        onRowOpen={(record) => navigate(`/cases/${record.id}`)}
        openPermission="cases.read"
        rowActions={rowActions}
        onDelete={(record) => setConfirming({ kind: 'deactivate', record })}
        deletePermission="cases.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(record) =>
          !record.isActive
            ? 'This case is already deleted.'
            : record.isRestricted
              ? 'This case is above your access level.'
              : !hasFlag(record.permissions, FLAG.delete)
                ? 'You have not been given Delete on this case.'
                : heldEvidenceReason(record.heldEvidenceCount)
        }
        otherInfo={(record) => record}
      />

      {creating || editing !== null ? (
        <CaseModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved, failed) => {
            const created = editing === null;

            setCreating(false);
            setEditing(null);
            toast.success(created ? `${saved.caseNumber} was registered.` : `${saved.caseNumber} was saved.`);
            // The case stands; a file that did not file is named, to attach again from the case.
            if (failed.length > 0) toast.failure(failedFilesMessage(failed));

            if (created) navigate(`/cases/${saved.id}`);
            else refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={(reauthentication) => void runConfirmed(reauthentication)}
          title={confirming.kind === 'deactivate' ? 'Delete case' : 'Restore case'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`case-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `${confirming.record.caseNumber} stops appearing in the case lists for everyone it is shared with.`
                  : `${confirming.record.caseNumber} appears in the case lists again for everyone it is shared with.`}
              </p>
              {confirming.kind === 'deactivate' ? (
                <p>
                  Nothing is erased. Its members, its evidence access log and every custody record are
                  kept, and a deleted case can be restored from this list.
                </p>
              ) : null}
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<CaseListItem>[] = [
  /*
    THE CASE NUMBER — TITLE, one anchor — the Evidence list's call, 09 Oct 2026: one line, one
    header; only the leading CODE is mono. A restricted case keeps its fixed mask and the
    screen-reader sentence saying why.
  */
  {
    key: 'caseNumber',
    header: 'Case',
    width: 420,
    flex: true,
    sortable: true,
    summary: true,
    render: (record) =>
      record.isRestricted ? (
        <span className="truncate">
          <span className="font-mono">{record.caseNumber}</span>
          {' — '}
          <span aria-hidden="true" className="font-mono text-slate-400">
            {record.title}
          </span>
          <span className="sr-only">Title withheld — above your access level</span>
        </span>
      ) : (
        <TextCell code={record.caseNumber} value={record.title} />
      ),
  },
  {
    key: 'accessLevel',
    header: 'Access level',
    width: 150,
    sortable: true,
    summary: true,
    render: (record) => <TextCell value={record.accessLevelName ?? 'Unclassified'} />,
  },
  {
    key: 'caseStatus',
    header: 'Case status',
    width: 140,
    stage: true,
    render: (record) => <StatusText tone="inactive">{CASE_STATUS_LABEL[record.status]}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (record) =>
      record.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
